import React, { useCallback, useEffect, useRef, useState } from 'react';
import { describeError } from '../../services/errorMessage';
import { ladeKickoffPlaene, ladeOffeneKickoffs } from '../../services/kickoff/kickoffService';
import type { UserProfile } from '../../types';
import { ProjectKickoffDialog } from './ProjectKickoffDialog';
import {
    KICKOFF_AKTUALISIEREN,
    sichtbareEintraege,
    spaeterAuswerten,
    verschobeneIds,
    type KickoffDialogEintrag,
    type SpaeterStand,
} from './kickoffDialogLogic';

/**
 * Entscheidet, ob das Kickoff-Popup erscheint (docs/projekt-kickoff-plan.md
 * §4.3), und hält die offenen Quittungen des angemeldeten Nutzers.
 *
 * Eigenständige Komponente mit eigenen Hooks, damit App.tsx keinen neuen Hook
 * bekommt: dort standen am 13.09. Effekte über den frühen Returns, die eine
 * erst darunter deklarierte Konstante schlossen (TDZ) — die App startete
 * nicht mehr. Das Gate braucht nur Werte, die im Haupt-Return sicher stehen.
 *
 * Fail-open: fehlt die Migration, liefert der Dienst eine leere Liste. Ein
 * echter Ladefehler (Netz) zeigt kein neues Popup; steht schon eines, bleibt
 * es mit seiner Liste stehen, verliert aber die Sperre (Störungsfall, siehe
 * `dialogModus`). Der nächste Start oder das Ereignis
 * `kickoff:aktualisieren` lädt neu.
 */
interface Props {
    userId?: string | null;
    role?: UserProfile['role'] | null;
}

/**
 * Nur interne Nutzer: die Rolle 'freelancer' hat keine Projekte im
 * Innendienst-Sinn (und landet in App.tsx ohnehin im eigenen Portal). Ohne
 * Profil (noch nicht geladen) erst recht nichts.
 *
 * `key={userId}`: Meldet sich im selben Tab ein anderer Nutzer an, beginnt
 * das Gate von vorn — die verschobenen und bestätigten Zeilen des Vorgängers
 * gehen den Neuen nichts an.
 */
export const ProjectKickoffGate: React.FC<Props> = ({ userId, role }) => {
    if (!userId || !role || role === 'freelancer') return null;
    return <KickoffGateFuerNutzer key={userId} userId={userId} />;
};

const KickoffGateFuerNutzer: React.FC<{ userId: string }> = ({ userId }) => {
    const [eintraege, setEintraege] = useState<KickoffDialogEintrag[]>([]);
    // In dieser Sitzung weg: per „Später“ verschoben oder bei einer Störung
    // ohne Zählen geschlossen. Bleiben bis zum nächsten App-Start weg, auch
    // wenn zwischendurch neu geladen wird.
    const [ausgeblendet, setAusgeblendet] = useState<ReadonlySet<string>>(() => new Set());
    // Technische Störung dieser Sitzung (Laden, Bestätigen oder Später
    // gescheitert). Bleibt bis zum nächsten Start stehen und hebt die Sperre
    // auf — eine Sperre ohne funktionierenden Ausweg machte die App
    // unbenutzbar (dialogModus in kickoffDialogLogic.ts).
    const [stoerung, setStoerung] = useState<string | null>(null);
    // Nur das Ergebnis des jüngsten Ladelaufs zählt; ein langsamer älterer
    // überschriebe sonst eine eben bestätigte Liste mit altem Stand.
    const ladeNr = useRef(0);
    // Hier bestätigte Zeilen: ein Ladelauf, der vor der Bestätigung losging,
    // brächte sie sonst noch einmal zurück.
    const erledigt = useRef<Set<string>>(new Set());

    useEffect(() => {
        // Nach dem Abbau (Abmelden) schreibt ein noch laufender Ladelauf nichts mehr.
        let abgebaut = false;
        const laden = async () => {
            const nr = ++ladeNr.current;
            try {
                const acks = await ladeOffeneKickoffs(userId);
                // Einträge mit `fehler` (Projekt nicht ladbar) kommen mit: der
                // Dialog zeigt sie ohne Bestätigen und behandelt sie als
                // Störung (dialogModus), damit sie nie sperren.
                const liste = acks.length > 0 ? await ladeKickoffPlaene(acks) : [];
                if (!abgebaut && nr === ladeNr.current) setEintraege(liste.filter(e => !erledigt.current.has(e.ack.id)));
            } catch (e) {
                // Die alte Liste bleibt stehen (sie ist nicht falsch, nur nicht
                // frisch) — aber die Sperre fällt, damit niemand an einem
                // Netzfehler hängen bleibt.
                console.warn('Kickoff: offene Projekte nicht ladbar —', describeError(e));
                if (!abgebaut) setStoerung(describeError(e));
            }
        };
        const neuLaden = () => { void laden(); };
        void laden();
        window.addEventListener(KICKOFF_AKTUALISIEREN, neuLaden);
        return () => {
            abgebaut = true;
            window.removeEventListener(KICKOFF_AKTUALISIEREN, neuLaden);
        };
    }, [userId]);

    const bestaetigt = useCallback((ackId: string) => {
        erledigt.current.add(ackId);
        setEintraege(prev => prev.filter(e => e.ack.id !== ackId));
        // Hub und Admin-Liste zeigen „bestätigt am …“ — sie hören auf dasselbe Ereignis.
        window.dispatchEvent(new Event(KICKOFF_AKTUALISIEREN));
    }, []);

    const verschoben = useCallback((gefragt: string[], staende: SpaeterStand[]) => {
        // Nur wirklich erhöhte Zeilen verschwinden; die übrigen bleiben mit
        // ihrem neuen Stand stehen und können sofort sperren (zweites Gerät).
        setEintraege(prev => spaeterAuswerten(prev, gefragt, staende).eintraege);
        const weg = verschobeneIds(gefragt, staende);
        if (weg.length > 0) setAusgeblendet(prev => new Set([...prev, ...weg]));
    }, []);

    const gestoert = useCallback((grund: string) => setStoerung(grund), []);

    // Nur im Störungsfall erreichbar: schließt, ohne etwas zu verbuchen.
    const geschlossen = useCallback((ids: string[]) => {
        setAusgeblendet(prev => new Set([...prev, ...ids]));
    }, []);

    const sichtbar = sichtbareEintraege(eintraege, ausgeblendet);
    if (sichtbar.length === 0) return null;

    return (
        <ProjectKickoffDialog
            eintraege={sichtbar}
            userId={userId}
            stoerung={stoerung}
            onBestaetigt={bestaetigt}
            onVerschoben={verschoben}
            onStoerung={gestoert}
            onSchliessen={geschlossen}
        />
    );
};
